import React, { useState } from 'react';
import {
  AppBar,
  Badge,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import PersonIcon from '@mui/icons-material/Person';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePushNotifications } from '../../contexts/PushNotificationContext';
import UserAccountMenu from './UserAccountMenu';

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const pushCtx = usePushNotifications();
  const unreadCount = pushCtx?.unreadCount ?? 0;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isAuthPage = location.pathname === '/auth';
  const isSplashPage = location.pathname === '/splash';
  const isLandingPage = location.pathname === '/' && !isSplashPage;

  const landingMenuItems = [
    { text: 'Home', href: '/#home' },
    { text: 'About', href: '/#about' },
    { text: 'Features', href: '/#features' },
    { text: 'How It Works', href: '/#how-it-works' }
  ];

  const appMenuItems = [
    { text: 'Events', icon: <DashboardIcon />, href: '/dashboard' },
    { text: 'Network', icon: <PeopleIcon />, href: '/network' },
    { text: 'Moments', icon: <PhotoLibraryIcon />, href: '/moments' },
    { text: 'Memory', icon: <FaceRetouchingNaturalIcon />, href: '/memory' },
    { text: 'Profile', icon: <PersonIcon />, href: '/profile' }
  ];

  const currentMenuItems = isLandingPage ? landingMenuItems : appMenuItems;

  const isSelected = (href) => {
    if (href.startsWith('/#')) {
      const section = href.substring(2);
      return location.hash === `#${section}`;
    }
    return location.pathname === href;
  };

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleAuth = () => {
    window.scrollTo(0, 0);
    navigate('/auth');
  };

  const handleHome = () => {
    window.scrollTo(0, 0);
    navigate('/');
  };

  const handleLogoClick = () => {
    window.scrollTo(0, 0);
    navigate('/');
  };

  const handleLogout = () => {
    logout();
    // replace: the page we're logging out from is behind PrivateRoute, so a
    // plain push would leave it in history for the back button to bounce
    // off of - see PrivateRoute.js's own comment.
    navigate('/auth', { replace: true });
  };

  const handleNavigation = (href) => {
    if (href.startsWith('/#')) {
      const element = document.getElementById(href.substring(2));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(href);
    }
  };

  const handleNotifications = () => navigate('/notifications');

  // ── Action icons shown when a user is logged in ────────────────────────────
  const LoggedInActions = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title="Notifications">
        <IconButton
          size="medium"
          onClick={handleNotifications}
          sx={{ color: 'text.primary' }}
          aria-label="notifications"
        >
          <Badge
            badgeContent={unreadCount > 0 ? unreadCount : null}
            color="error"
            max={99}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <UserAccountMenu />
    </Box>
  );

  const drawer = (
    <Box
      sx={{
        width: 250,
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton onClick={toggleDrawer(false)}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {currentMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton onClick={() => handleNavigation(item.href)}>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        {user ? (
          <UserAccountMenu />
        ) : isAuthPage ? (
          <Button
            fullWidth
            variant="contained"
            onClick={handleHome}
            startIcon={<HomeIcon />}
          >
            Go Home
          </Button>
        ) : (
          <>
            <Button
              fullWidth
              variant="contained"
              onClick={handleAuth}
              sx={{ mb: 1 }}
            >
              Login
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleAuth}
            >
              Sign Up
            </Button>
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backdropFilter: 'blur(10px)',
        backgroundColor: '#fffc',
        borderBottom: 1,
        borderColor: 'divider',
        borderRadius: 0
      }}
    >
      <Container maxWidth={false}>
        <Toolbar disableGutters>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              mr: 2
            }}
            onClick={handleLogoClick}
          >
            <img
              src="/logo.png"
              alt="CircleMoments Logo"
              style={{ height: '40px', marginRight: '8px' }}
            />
            <Typography
              variant="h6"
              noWrap
              sx={{ fontWeight: 700, color: 'text.primary', textDecoration: 'none' }}
            >
              CircleMoments
            </Typography>
          </Box>

          {isMobile ? (
            <Box sx={{ ml: 'auto' }}>
              {isLandingPage ? (
                <IconButton size="large" aria-label="menu" onClick={toggleDrawer(true)}>
                  <MenuIcon />
                </IconButton>
              ) : user ? (
                <LoggedInActions />
              ) : isAuthPage ? (
                <IconButton size="large" onClick={handleHome}>
                  <HomeIcon />
                </IconButton>
              ) : (
                <Button variant="contained" onClick={handleAuth}>Login</Button>
              )}
            </Box>
          ) : (
            <>
              <Box sx={{
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'center',
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)'
              }}>
                <Box sx={{
                  display: 'flex',
                  gap: 1,
                  bgcolor: 'rgba(0, 0, 0, 0.04)',
                  borderRadius: 2,
                  p: 0.5
                }}>
                  {currentMenuItems.map((item) => (
                    <Button
                      key={item.text}
                      onClick={() => handleNavigation(item.href)}
                      startIcon={item.icon}
                      sx={{
                        color: 'text.primary',
                        borderRadius: 1.5,
                        px: 2,
                        py: 1,
                        ...(isSelected(item.href) && {
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': { bgcolor: 'primary.dark' }
                        })
                      }}
                    >
                      {item.text}
                    </Button>
                  ))}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                {user ? (
                  <LoggedInActions />
                ) : isAuthPage ? (
                  <Button variant="contained" onClick={handleHome} startIcon={<HomeIcon />}>
                    Go Home
                  </Button>
                ) : (
                  <>
                    <Button variant="outlined" onClick={handleAuth}>Login</Button>
                    <Button variant="contained" onClick={handleAuth}>Sign Up</Button>
                  </>
                )}
              </Box>
            </>
          )}
        </Toolbar>
      </Container>

      {isLandingPage && (
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={toggleDrawer(false)}
          PaperProps={{
            sx: {
              backdropFilter: 'blur(10px)',
              backgroundColor: '#fffc',
              borderRadius: 0
            }
          }}
        >
          {drawer}
        </Drawer>
      )}
    </AppBar>
  );
};

export default Header;
